/**
 * / — authenticated home. Renders the org-wide master Kanban
 * (`<MasterBoardPage>`) by default. Aggregates every card from every
 * project the caller is a member of into the canonical 5-column shape.
 *
 * Pre-2026-04-30 this route auto-redirected to the first project's
 * board; the master dashboard supersedes that as the default landing
 * surface (per legacy parity — `Board.jsx` was the org-wide aggregator
 * the legacy site loaded at `/`).
 *
 * No-auth-token → bounce to /login. 401 from /api/me → bounce. The
 * master page's own loading/empty/error states cover the rest.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import { ApiError } from '@/api/client'
import type { DeliverableKind } from '@/api/types-bridge'
import { getAccessToken } from '@/auth/cookie-helpers'
import {
  type ListViewSearch,
  parseListViewSearch,
} from '@/features/board/components/list/use-list-view-state'
import { MasterBoardPage } from '@/features/dashboard/MasterBoardPage'
import { type MasterLayout, parseMasterLayout } from '@/features/dashboard/master-layout'
import {
  MASTER_VIEW_LABELS,
  type MasterViewMode,
} from '@/features/dashboard/use-master-filter-state'
import { useMe } from '@/features/me/use-me'

const KIND_VALUES = new Set<DeliverableKind>(['task', 'ticket', 'event', 'announcement'])
const VIEW_VALUES = new Set<MasterViewMode>(Object.keys(MASTER_VIEW_LABELS) as MasterViewMode[])

function parseStringList(raw: unknown): readonly string[] | undefined {
  const candidates: string[] = []
  if (Array.isArray(raw)) {
    for (const v of raw) {
      if (typeof v === 'string') candidates.push(v)
    }
  } else if (typeof raw === 'string' && raw.length > 0) {
    candidates.push(...raw.split(','))
  } else {
    return undefined
  }
  const out = candidates.map((s) => s.trim()).filter((s) => s.length > 0)
  return out.length > 0 ? out : undefined
}

function parseKinds(raw: unknown): readonly DeliverableKind[] | undefined {
  const list = parseStringList(raw)
  if (!list) return undefined
  const out: DeliverableKind[] = []
  for (const v of list) {
    const lower = v.toLowerCase()
    if (KIND_VALUES.has(lower as DeliverableKind)) out.push(lower as DeliverableKind)
  }
  return out.length > 0 ? out : undefined
}

export interface MasterSearch extends Omit<ListViewSearch, 'layout'> {
  /**
   * The All Projects board's layout switcher value. Widened past
   * `ListViewSearch['layout']` (`'board' | 'list'`, shared with the
   * per-project board's own Board/List toggle) to the full 5-value
   * `MasterLayout` union — `parseMasterLayout` (not `parseListViewSearch`)
   * parses it below, treating legacy `'board'` as unrecognised so it
   * falls through to the account preference rather than an explicit
   * default.
   */
  layout?: MasterLayout
  /**
   * Jira-style "selected deliverable" key. Persists in the URL while
   * the master detail modal is open so a refresh / paste re-opens the
   * same card. Shorthand for tasks/tickets/events; uuid for
   * announcements (no shorthand).
   */
  selectedDeliverable?: string
  /** Comment to scroll to inside the opened card; consumed once, then stripped. */
  commentId?: string
  /** Top-level owner of `commentId` when it is a reply; resolves the thread without a lookup. */
  parentCommentId?: string
  /** Built-in master view mode. */
  view?: MasterViewMode
  /** Comma-separated subset of `task,ticket,event,announcement`. */
  kinds?: readonly DeliverableKind[]
  /** User ids; literal `'unassigned'` matches cards with no assignee. */
  assignees?: readonly string[]
  /** Label NAMES (cross-project union by name). */
  labels?: readonly string[]
  /** Event/announcement attendee user ids. */
  attendees?: readonly string[]
  /** Free-text search query. */
  q?: string
}

export const Route = createFileRoute('/')({
  component: HomePage,
  validateSearch: (search: Record<string, unknown>): MasterSearch => ({
    // List-view params (`sort`, `dir`, `page`, `l*` filters) are parsed by
    // the shared helper so both board surfaces stay in step. `layout` is
    // NOT — the master route owns its own 5-value parse rather than the
    // per-project-shared 2-value `BoardLayout` one, so the spread below
    // is deliberately followed by an override.
    ...parseListViewSearch(search),
    layout: parseMasterLayout(search.layout),
    selectedDeliverable:
      typeof search.selectedDeliverable === 'string' ? search.selectedDeliverable : undefined,
    commentId: typeof search.commentId === 'string' ? search.commentId : undefined,
    parentCommentId:
      typeof search.parentCommentId === 'string' ? search.parentCommentId : undefined,
    view:
      typeof search.view === 'string' && VIEW_VALUES.has(search.view as MasterViewMode)
        ? (search.view as MasterViewMode)
        : undefined,
    kinds: parseKinds(search.kinds),
    assignees: parseStringList(search.assignees),
    labels: parseStringList(search.labels),
    attendees: parseStringList(search.attendees),
    q: typeof search.q === 'string' && search.q.length > 0 ? search.q : undefined,
  }),
})

function HomePage() {
  const navigate = useNavigate()
  const me = useMe()

  // No token at all? Skip the API roundtrip and bounce immediately.
  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  // 401 → bounce. Other /api/me errors fall through to the master
  // page's own error state (which will surface its own dashboard
  // fetch error).
  useEffect(() => {
    if (me.error instanceof ApiError && me.error.status === 401) {
      navigate({ to: '/login' })
    }
  }, [me.error, navigate])

  if (!getAccessToken()) return null

  return <MasterBoardPage />
}
